import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  CreditCard,
  FileText,
  ShieldCheck,
  ShieldX,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert } from '@/components/ui/alert'
import { vendorsApi } from '@/lib/api/vendors'
import type { Vendor } from '@portal/types'

interface VendorDetailProps {
  vendorId: string | null
  open: boolean
  onClose: () => void
  onEdit: (vendor: Vendor) => void
}

const formatAmount = (paise: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(paise / 100)

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

function statusBadge(status: string) {
  const map: Record<string, string> = {
    SUCCESS: 'text-emerald-700 border-emerald-300 bg-emerald-50',
    FAILED: 'text-red-600 border-red-300 bg-red-50',
    PENDING_APPROVAL: 'text-amber-600 border-amber-300 bg-amber-50',
    PROCESSING: 'text-blue-600 border-blue-300 bg-blue-50',
    REJECTED: 'text-red-600 border-red-300 bg-red-50',
    REVERSED: 'text-orange-600 border-orange-300 bg-orange-50',
    CANCELLED: 'text-slate-500 border-slate-300 bg-slate-50',
  }
  return (
    <Badge variant="outline" className={`text-xs ${map[status] ?? 'text-slate-500 border-slate-300'}`}>
      {status.replace('_', ' ')}
    </Badge>
  )
}

function categoryLabel(cat: string) {
  const map: Record<string, string> = {
    SUPPLIER: 'Supplier',
    CONTRACTOR: 'Contractor',
    UTILITY: 'Utility',
    RENT: 'Rent',
    OTHER: 'Other',
  }
  return map[cat] ?? cat
}

export default function VendorDetail({ vendorId, open, onClose, onEdit }: VendorDetailProps) {
  const queryClient = useQueryClient()
  const [beneMsg, setBeneMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: vendorRes, isLoading } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => vendorsApi.get(vendorId!),
    enabled: !!vendorId && open,
  })

  const vendor = vendorRes?.data

  const registerMutation = useMutation({
    mutationFn: () => vendorsApi.registerBene(vendorId!),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      setBeneMsg({ type: 'success', text: res.data?.message ?? 'Bene registered successfully' })
    },
    onError: (err: any) => {
      setBeneMsg({ type: 'error', text: err?.error ?? 'Bene registration failed' })
    },
  })

  const validateMutation = useMutation({
    mutationFn: () => vendorsApi.validateBene(vendorId!),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      const d = res.data
      setBeneMsg({
        type: d?.isValid ? 'success' : 'error',
        text: d?.message ?? (d?.isValid ? 'Bene validated' : 'Bene not found'),
      })
    },
    onError: (err: any) => {
      setBeneMsg({ type: 'error', text: err?.error ?? 'Bene validation failed' })
    },
  })

  function handleClose() {
    setBeneMsg(null)
    onClose()
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Vendor Details
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-40" />
          </div>
        ) : !vendor ? (
          <p className="text-sm text-muted-foreground">Vendor not found.</p>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold">{vendor.name}</h2>
                {vendor.nickname && (
                  <p className="text-sm text-muted-foreground">{vendor.nickname}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Badge variant="secondary">{categoryLabel(vendor.category)}</Badge>
                {!vendor.isActive && (
                  <Badge variant="outline" className="text-slate-500 border-slate-300">
                    Inactive
                  </Badge>
                )}
              </div>
            </div>

            {/* Bank Details */}
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <CreditCard className="h-4 w-4" />
                Bank Details
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Account</p>
                  <p className="font-mono">{vendor.bankAccountMasked}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">IFSC</p>
                  <p className="font-mono">{vendor.bankIfsc}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p>{vendor.payeeType === 'WITHIN_BANK' ? 'Within ICICI (FT)' : 'Other Bank'}</p>
                </div>
                {vendor.upiVpa && (
                  <div>
                    <p className="text-xs text-muted-foreground">UPI VPA</p>
                    <p>{vendor.upiVpa}</p>
                  </div>
                )}
                {vendor.gstin && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">GSTIN</p>
                    <p className="font-mono">{vendor.gstin}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ICICI Bene Registration */}
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                {vendor.beneId ? (
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                ) : (
                  <ShieldX className="h-4 w-4 text-muted-foreground" />
                )}
                ICICI Bene Registration
              </div>

              {vendor.beneId ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="text-sm text-emerald-700">
                    Registered — BNF ID: <span className="font-mono">{vendor.beneId}</span>
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="text-sm text-muted-foreground">Not registered with ICICI</span>
                </div>
              )}

              {beneMsg && (
                <Alert
                  variant={beneMsg.type === 'error' ? 'destructive' : 'default'}
                  className="text-sm py-2 px-3"
                >
                  {beneMsg.text}
                </Alert>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={registerMutation.isPending || validateMutation.isPending}
                  onClick={() => {
                    setBeneMsg(null)
                    registerMutation.mutate()
                  }}
                >
                  {registerMutation.isPending ? 'Registering…' : vendor.beneId ? 'Re-register Bene' : 'Register Bene'}
                </Button>
                {vendor.beneId && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={registerMutation.isPending || validateMutation.isPending}
                    onClick={() => {
                      setBeneMsg(null)
                      validateMutation.mutate()
                    }}
                  >
                    {validateMutation.isPending ? 'Validating…' : 'Validate Bene'}
                  </Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Note: 30-min cooling period applies after registration. Mandatory only for
                Nodal/Escrow accounts.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleClose()
                  onEdit(vendor)
                }}
              >
                Edit Vendor
              </Button>
            </div>

            <Separator />

            {/* Payment History */}
            <div>
              <div className="flex items-center gap-2 mb-3 text-sm font-medium">
                <FileText className="h-4 w-4" />
                Payment History
              </div>

              {!vendor.paymentHistory?.length ? (
                <p className="text-sm text-muted-foreground py-2">No payments yet.</p>
              ) : (
                <div className="space-y-2">
                  {vendor.paymentHistory.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{p.payeeName}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.paymentMode} · {formatDate(p.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-medium">{formatAmount(p.amount)}</span>
                        {statusBadge(p.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
